import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import ClientSearchPicker from "../common/ClientSearchPicker";
import { scheduleUrl } from "../../../utils/adminNav";
import { toDateInputValue } from "../../../utils/appointmentLabels";
import L from "../../../constants/baysportLabels";
import GroupSchedulePanel from "./GroupSchedulePanel";
import GroupLeaderboardPanel from "../common/GroupLeaderboardPanel";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import aa from "../AdminPortal/adminApp.module.css";
import SwipeableRow from "../AdminPortal/SwipeableRow";
import {
  HubDetailPanel,
  HubFormCard,
  HubListGroup,
  HubNavAdd,
  HubRow,
  HubScreen,
  HubSegment,
  HubSheet,
  HubSheetActions,
  HubToolbar,
} from "../AdminPortal/HubPwa";
import s from "../clinic.module.css";
import {
  Button,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonCard,
  useToast,
} from "../../ui";
import { IconCalendar, IconUsers } from "../../ui/icons";

function capacityPct(count, max) {
  if (!max) return null;
  return Math.min(100, Math.round((count / max) * 100));
}

function groupBranchName(group, branches) {
  if (group?.branch?.name) return group.branch.name;
  const id = group?.branch_id || group?.branch?.id;
  if (!id) return null;
  return branches.find((b) => b.id === id)?.name || null;
}

export default function AdminGroups({ staffMode = false }) {
  const toast = useToast();
  const navigate = useNavigate();
  const isApp = useAdminHubUi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [branches, setBranches] = useState([]);
  const [directions, setDirections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", direction_id: "", branch_id: "" });
  const [members, setMembers] = useState([]);
  const [memberPatientId, setMemberPatientId] = useState("");
  const [editBranchId, setEditBranchId] = useState("");
  const [savingBranch, setSavingBranch] = useState(false);
  const [pollAuto, setPollAuto] = useState(false);
  const [pollHours, setPollHours] = useState(2);
  const [savingPoll, setSavingPoll] = useState(false);
  const [detailTab, setDetailTab] = useState("members");
  const panelFromUrl = searchParams.get("panel");
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveFutureCount, setArchiveFutureCount] = useState(0);
  const [archiveMode, setArchiveMode] = useState("cancel");
  const [archiveTransferId, setArchiveTransferId] = useState("");
  const [archiveLoading, setArchiveLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsRes, branchRes, dirRes] = await Promise.all([
        clinicAPI.getSportGroupsSummary(),
        clinicAPI.getBranches(),
        clinicAPI.getSportDirections(),
      ]);
      const list = groupsRes.groups || [];
      setGroups(list);
      const branchList = (branchRes.branches || []).filter(
        (b) => b.is_active !== false,
      );
      setBranches(branchList);
      const dirs = dirRes.directions || [];
      setDirections(dirs);
      if (dirs.length === 1) {
        setForm((f) => ({ ...f, direction_id: dirs[0].id }));
      }
      if (branchList.length === 1) {
        setForm((f) => ({ ...f, branch_id: branchList[0].id }));
      }
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const activeGroups = useMemo(
    () => groups.filter((g) => g.is_active !== false),
    [groups],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (isApp) return;
    if (activeGroups.length && !selectedId) {
      setSelectedId(activeGroups[0].id);
    }
  }, [activeGroups, selectedId, isApp]);

  useEffect(() => {
    if (panelFromUrl === "schedule") {
      setDetailTab("schedule");
    }
  }, [panelFromUrl]);

  const selected = useMemo(
    () => activeGroups.find((g) => g.id === selectedId) || null,
    [activeGroups, selectedId],
  );

  useEffect(() => {
    if (!selected) return;
    setEditBranchId(
      selected.branch_id || selected.branch?.id || branches[0]?.id || "",
    );
    setPollAuto(!!selected.attendance_poll_auto);
    setPollHours(selected.attendance_poll_hours_before ?? 2);
  }, [selected, branches]);

  const loadMembers = useCallback(async (groupId) => {
    if (!groupId) return;
    try {
      const data = await clinicAPI.getGroupMembers(groupId);
      setMembers(data.members || []);
    } catch {
      setMembers([]);
    }
  }, []);

  useEffect(() => {
    if (selectedId) loadMembers(selectedId);
    else setMembers([]);
  }, [selectedId, loadMembers]);

  const defaultForm = useCallback(
    () => ({
      name: "",
      direction_id: directions.length === 1 ? directions[0].id : "",
      branch_id: branches.length === 1 ? branches[0].id : branches[0]?.id || "",
    }),
    [directions, branches],
  );

  const openAddForm = () => {
    setForm(defaultForm());
    setShowAdd(true);
  };

  const closeAddForm = () => setShowAdd(false);

  const canCreate =
    directions.length > 0 &&
    branches.length > 0 &&
    form.name.trim() &&
    (form.direction_id || directions.length === 1);

  const createGroup = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    try {
      const r = await clinicAPI.createSpecialization({
        name: form.name.trim(),
        direction_id: form.direction_id || directions[0]?.id,
        branch_id: form.branch_id || branches[0]?.id,
      });
      setForm(defaultForm());
      setShowAdd(false);
      toast.success("Группа создана");
      const newId = r.specialization?.id;
      if (newId) setSelectedId(newId);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const openArchiveFlow = async (g) => {
    if (staffMode) return;
    setArchiveTarget(g);
    setArchiveMode("cancel");
    setArchiveTransferId("");
    setArchiveLoading(true);
    try {
      const preview = await clinicAPI.getSpecializationArchivePreview(g.id);
      setArchiveFutureCount(preview.future_sessions ?? 0);
    } catch {
      setArchiveFutureCount(0);
    } finally {
      setArchiveLoading(false);
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    if (archiveMode === "transfer" && !archiveTransferId) {
      toast.error("Выберите группу для переноса");
      return;
    }
    try {
      const opts = {};
      if (archiveMode === "transfer") {
        opts.transfer_to_group_id = archiveTransferId;
      }
      const r = await clinicAPI.deactivateSpecialization(archiveTarget.id, opts);
      toast.success(r.message || "Группа в архиве");
      if (selectedId === archiveTarget.id) setSelectedId(null);
      setArchiveTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось архивировать");
    }
  };

  const renderArchiveSheet = () => (
    <HubSheet
      open={Boolean(archiveTarget)}
      onClose={() => setArchiveTarget(null)}
      title={archiveTarget ? `Архив: ${archiveTarget.name}` : "Архив группы"}
      subtitle={
        archiveLoading
          ? "Загрузка…"
          : archiveFutureCount > 0
            ? `${archiveFutureCount} будущих занятий в расписании`
            : "Будущих занятий нет"
      }
      footer={
        !archiveLoading ? (
          <HubSheetActions
            primary="Архивировать"
            onPrimary={confirmArchive}
            onSecondary={() => setArchiveTarget(null)}
          />
        ) : null
      }
    >
      {archiveLoading ? (
        <p className={aa.hubSheetHint}>Проверяем расписание…</p>
      ) : archiveFutureCount > 0 ? (
        <>
          <p className={aa.archiveSheetLead}>
            Группа будет скрыта из списка. Что сделать с занятиями этой группы?
          </p>
          <div className={aa.archiveChoiceList}>
            <button
              type="button"
              className={
                archiveMode === "cancel"
                  ? aa.archiveChoiceOn
                  : aa.archiveChoice
              }
              onClick={() => setArchiveMode("cancel")}
            >
              <strong>Отменить занятия</strong>
              <span>
                Все {archiveFutureCount} будущих занятий будут отменены
              </span>
            </button>
            <button
              type="button"
              className={
                archiveMode === "transfer"
                  ? aa.archiveChoiceOn
                  : aa.archiveChoice
              }
              onClick={() => setArchiveMode("transfer")}
            >
              <strong>Перенести в другую группу</strong>
              <span>Занятия останутся в расписании под новой группой</span>
            </button>
          </div>
          {archiveMode === "transfer" ? (
            <Field label="Группа для переноса">
              <Select
                value={archiveTransferId}
                onChange={(e) => setArchiveTransferId(e.target.value)}
              >
                <option value="">Выберите группу</option>
                {activeGroups
                  .filter((g) => g.id !== archiveTarget?.id)
                  .map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
              </Select>
            </Field>
          ) : null}
        </>
      ) : (
        <p className={aa.hubSheetHint}>
          Группа будет скрыта из списка. Расписание не затронуто — будущих
          занятий нет.
        </p>
      )}
    </HubSheet>
  );

  const savePollSettings = async () => {
    if (!selectedId) return;
    setSavingPoll(true);
    try {
      await clinicAPI.updateSpecialization(selectedId, {
        attendance_poll_auto: pollAuto,
        attendance_poll_hours_before: pollHours,
      });
      toast.success("Настройки явки сохранены");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSavingPoll(false);
    }
  };

  const saveBranch = async () => {
    if (!selectedId || !editBranchId) {
      toast.error("Выберите зал");
      return;
    }
    setSavingBranch(true);
    try {
      await clinicAPI.updateSpecialization(selectedId, {
        branch_id: editBranchId,
      });
      toast.success("Зал обновлён");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSavingBranch(false);
    }
  };

  const addMember = async () => {
    if (!selectedId || !memberPatientId) return;
    try {
      await clinicAPI.addGroupMember(selectedId, memberPatientId);
      setMemberPatientId("");
      loadMembers(selectedId);
      load();
      toast.success("Добавлен в группу");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const setTab = (tab) => {
    setDetailTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === "schedule") next.set("panel", "schedule");
    else next.delete("panel");
    setSearchParams(next, { replace: true });
  };

  if (loading) {
    return (
      <div className={s.groupsHub}>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (directions.length === 0 || branches.length === 0) {
    return (
      <div className={s.groupsHubEmpty}>
        <div className={s.groupsHubEmptyIcon}>👥</div>
        <h2 className={s.groupsHubTitle}>{L.groups}</h2>
        <p className={s.groupsHubLead}>
          Сначала добавьте направление и зал в структуре секции — без них
          группы не создать.
        </p>
        <Link to="/manage/app/directions" className={s.groupsHubCta}>
          Настроить направления и залы →
        </Link>
      </div>
    );
  }

  const dirName = (g) =>
    g.direction?.name ||
    g.sport_direction ||
    directions.find((d) => d.id === g.direction_id)?.name;

  const groupCountLabel = `${activeGroups.length} ${
    activeGroups.length === 1
      ? "группа"
      : activeGroups.length < 5
        ? "группы"
        : "групп"
  }`;

  const groupAddFooter = (
    <HubSheetActions
      primary="Создать"
      primaryForm="groups-add-form"
      primaryDisabled={!canCreate}
      onSecondary={closeAddForm}
    />
  );

  const renderAddGroupSheet = () => (
    <HubSheet
      open={showAdd}
      onClose={closeAddForm}
      title="Новая группа"
      subtitle="Направление, зал и название — расписание настроите позже"
      titleId="groups-add-title"
      footer={groupAddFooter}
    >
      <form id="groups-add-form" onSubmit={createGroup}>
        <Field label="Название">
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Например, Группа 2010"
            required
            autoFocus
          />
        </Field>
        <Field label="Направление">
          <Select
            value={form.direction_id}
            onChange={(e) =>
              setForm({ ...form, direction_id: e.target.value })
            }
            required={directions.length > 1}
          >
            {directions.length > 1 ? (
              <option value="">Выберите направление</option>
            ) : null}
            {directions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Зал">
          <Select
            value={form.branch_id || branches[0]?.id || ""}
            onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
          >
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
      </form>
    </HubSheet>
  );

  const detailTabs = staffMode
    ? [
        { id: "members", label: "Состав" },
        { id: "ranking", label: "Рейтинг" },
        { id: "schedule", label: "Расписание" },
      ]
    : [
        { id: "members", label: "Состав" },
        { id: "ranking", label: "Рейтинг" },
        { id: "schedule", label: "Расписание" },
        { id: "notify", label: "Явка" },
        { id: "venue", label: "Зал" },
      ];

  const openGroupCalendar = () => {
    if (!selected) return;
    navigate(
      scheduleUrl({
        groupId: selected.id,
        date: toDateInputValue(),
        view: "month",
      }),
    );
  };

  const renderGroupDetail = () => {
    if (!selected) return null;
    return (
      <>
        <div className={aa.hubInlineActions}>
          <button
            type="button"
            className={aa.hubInlineAction}
            onClick={openGroupCalendar}
          >
            <IconCalendar width={16} height={16} />
            Календарь
          </button>
        </div>

        {detailTab === "venue" ? (
          <HubFormCard title="Зал для группы">
            <Select
              value={editBranchId}
              onChange={(e) => setEditBranchId(e.target.value)}
            >
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
            <Button
              type="button"
              size="sm"
              disabled={
                savingBranch ||
                editBranchId === (selected.branch_id || selected.branch?.id)
              }
              onClick={saveBranch}
            >
              {savingBranch ? "…" : "Сохранить зал"}
            </Button>
          </HubFormCard>
        ) : detailTab === "schedule" ? (
          <GroupSchedulePanel
            groupId={selected.id}
            groupName={selected.name}
            directionName={dirName(selected)}
          />
        ) : detailTab === "notify" ? (
          <HubFormCard title="Опрос о явке">
            <p className={aa.hubHint}>
              За {pollHours} ч до групповой тренировки клиентам придёт push и
              опрос в приложении. Если занятие добавили позже — отправьте опрос
              вручную из календаря.
            </p>
            <label className={aa.trainerSettingsToggle}>
              <input
                type="checkbox"
                checked={pollAuto}
                onChange={(e) => setPollAuto(e.target.checked)}
              />
              <span>Авто-опрос перед тренировкой</span>
            </label>
            <Field label="За сколько часов">
              <Input
                type="number"
                min={1}
                max={24}
                value={pollHours}
                disabled={!pollAuto}
                onChange={(e) => setPollHours(e.target.value)}
              />
            </Field>
            <Button
              type="button"
              size="sm"
              disabled={savingPoll}
              onClick={savePollSettings}
            >
              {savingPoll ? "…" : "Сохранить"}
            </Button>
          </HubFormCard>
        ) : detailTab === "ranking" ? (
          <GroupLeaderboardPanel groupId={selected.id} />
        ) : (
          <>
            <HubFormCard title="Добавить спортсмена">
              <ClientSearchPicker
                value={memberPatientId}
                onChange={setMemberPatientId}
              />
              <Button
                type="button"
                disabled={!memberPatientId}
                onClick={addMember}
              >
                Добавить
              </Button>
            </HubFormCard>
            {members.length === 0 ? (
              <p className={s.groupsHubMembersEmpty}>
                Пока никого — найдите клиента выше
              </p>
            ) : (
              <ul className={aa.hubMemberList}>
                {members.map((m) => (
                  <li key={m.id} className={aa.hubMemberRow}>
                    <Link
                      to={`/manage/app/clients/${m.patient_id}`}
                      className={aa.hubMemberName}
                    >
                      {m.patient?.full_name || "Клиент"}
                    </Link>
                    <button
                      type="button"
                      className={aa.hubMemberAction}
                      onClick={async () => {
                        await clinicAPI.removeGroupMember(
                          selected.id,
                          m.patient_id,
                        );
                        loadMembers(selected.id);
                        load();
                        toast.success("Убран из группы");
                      }}
                    >
                      Убрать
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </>
    );
  };

  if (isApp) {
    if (selectedId && selected) {
      return (
        <HubScreen
          title={selected.name}
          onBack={() => {
            setSelectedId(null);
            setDetailTab("members");
          }}
        >
          <p className={aa.hubRowMeta} style={{ margin: "0 0 8px" }}>
            {[dirName(selected), groupBranchName(selected, branches) || "Зал не указан"]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <HubSegment tabs={detailTabs} active={detailTab} onChange={setTab} />
          <HubDetailPanel>{renderGroupDetail()}</HubDetailPanel>
        </HubScreen>
      );
    }

    return (
      <HubScreen
        title={L.groups}
        right={
          staffMode ? null : (
            <HubNavAdd onClick={openAddForm} ariaLabel="Новая группа" />
          )
        }
      >
        {groupCountLabel ? <HubToolbar countLabel={groupCountLabel} /> : null}
        {activeGroups.length === 0 ? (
          <EmptyState
            title="Пока нет групп"
            description="Создайте первую — детскую, взрослую или соревновательную"
            actionLabel="Новая группа"
            onAction={openAddForm}
          />
        ) : (
          <HubListGroup>
            {activeGroups.map((g) => {
              const count = g.member_count ?? 0;
              const max = g.max_capacity;
              const pct = capacityPct(count, max);
              const venue = groupBranchName(g, branches);
              return staffMode ? (
                <HubRow
                  key={g.id}
                  title={g.name}
                  meta={[dirName(g), venue || "Зал не указан"]
                    .filter(Boolean)
                    .join(" · ")}
                  sub={
                    max
                      ? `${count} / ${max} спортсменов`
                      : `${count} спортсменов`
                  }
                  progress={pct}
                  onClick={() => {
                    setSelectedId(g.id);
                    setDetailTab("members");
                  }}
                />
              ) : (
                <SwipeableRow
                  key={g.id}
                  actions={[
                    {
                      id: "archive",
                      label: "Архив",
                      tone: "danger",
                      onClick: () => openArchiveFlow(g),
                    },
                  ]}
                >
                  <HubRow
                    title={g.name}
                    meta={[dirName(g), venue || "Зал не указан"]
                      .filter(Boolean)
                      .join(" · ")}
                    sub={
                      max
                        ? `${count} / ${max} спортсменов`
                        : `${count} спортсменов`
                    }
                    progress={pct}
                    onClick={() => {
                      setSelectedId(g.id);
                      setDetailTab("members");
                    }}
                  />
                </SwipeableRow>
              );
            })}
          </HubListGroup>
        )}
        {!staffMode ? renderAddGroupSheet() : null}
        {!staffMode ? renderArchiveSheet() : null}
      </HubScreen>
    );
  }

  return (
    <div className={s.groupsHub}>
      <header className={s.groupsHubHead}>
        <div>
          <h1 className={s.groupsHubTitle}>{L.groups}</h1>
          <p className={s.groupsHubLead}>
            Группа объединяет направление, зал и спортсменов. Зал и расписание
            можно изменить в любой момент.
          </p>
        </div>
        <Button type="button" onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? "Закрыть" : "+ Новая группа"}
        </Button>
      </header>

      {showAdd ? (
        <form onSubmit={createGroup} className={s.groupsHubCreate}>
          <div className={s.groupsHubCreateFields}>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Название, напр. Младшая 7–10 лет"
              required
              autoFocus
            />
            {directions.length > 1 ? (
              <Select
                value={form.direction_id}
                onChange={(e) =>
                  setForm({ ...form, direction_id: e.target.value })
                }
              >
                <option value="">Направление</option>
                {directions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
            ) : null}
            <Select
              value={form.branch_id || branches[0]?.id || ""}
              onChange={(e) =>
                setForm({ ...form, branch_id: e.target.value })
              }
            >
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </div>
          <Button type="submit" disabled={!canCreate}>
            Создать группу
          </Button>
        </form>
      ) : null}

      {activeGroups.length === 0 ? (
        <EmptyState
          title="Пока нет групп"
          description="Создайте первую — детскую, взрослую или соревновательную"
          actionLabel="+ Новая группа"
          onAction={() => setShowAdd(true)}
        />
      ) : (
        <div className={s.groupsHubLayout}>
          <aside className={s.groupsHubList} aria-label="Список групп">
            {activeGroups.map((g) => {
              const count = g.member_count ?? 0;
              const max = g.max_capacity;
              const pct = capacityPct(count, max);
              const isSel = g.id === selectedId;
              const venue = groupBranchName(g, branches);
              return (
                <button
                  key={g.id}
                  type="button"
                  className={
                    isSel ? s.groupsHubCardActive : s.groupsHubCard
                  }
                  onClick={() => {
                    setSelectedId(g.id);
                    setDetailTab("members");
                  }}
                >
                  <span className={s.groupsHubCardIcon}>👥</span>
                  <span className={s.groupsHubCardBody}>
                    <strong className={s.groupsHubCardName}>{g.name}</strong>
                    <span className={s.groupsHubCardMeta}>
                      {[dirName(g), venue || "Зал не указан"]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    <span className={s.groupsHubCardCount}>
                      <IconUsers width={14} height={14} />
                      {count}
                      {max ? ` / ${max}` : ""} спортсменов
                    </span>
                    {pct != null ? (
                      <span className={s.groupsHubCardBar} aria-hidden>
                        <span
                          className={s.groupsHubCardBarFill}
                          style={{ width: `${pct}%` }}
                        />
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </aside>

          <section className={s.groupsHubDetail}>
            {!selected ? (
              <div className={s.groupsHubDetailEmpty}>
                <p>Выберите группу слева</p>
              </div>
            ) : (
              <>
                <header className={s.groupsHubDetailHead}>
                  <div>
                    <h2 className={s.groupsHubDetailTitle}>{selected.name}</h2>
                    <p className={s.groupsHubDetailSub}>
                      {dirName(selected)} ·{" "}
                      {groupBranchName(selected, branches) || "Зал не указан"}
                    </p>
                  </div>
                  <div className={s.groupsHubDetailActions}>
                    <Button type="button" size="sm" onClick={openGroupCalendar}>
                      <IconCalendar width={16} height={16} />
                      Календарь
                    </Button>
                    {!staffMode ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => openArchiveFlow(selected)}
                      >
                        В архив
                      </Button>
                    ) : null}
                  </div>
                </header>

                <div className={s.groupsHubDetailTabs} role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={detailTab === "members"}
                    className={
                      detailTab === "members"
                        ? s.groupsHubDetailTabActive
                        : s.groupsHubDetailTab
                    }
                    onClick={() => setTab("members")}
                  >
                    Состав
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={detailTab === "ranking"}
                    className={
                      detailTab === "ranking"
                        ? s.groupsHubDetailTabActive
                        : s.groupsHubDetailTab
                    }
                    onClick={() => setTab("ranking")}
                  >
                    Рейтинг
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={detailTab === "schedule"}
                    className={
                      detailTab === "schedule"
                        ? s.groupsHubDetailTabActive
                        : s.groupsHubDetailTab
                    }
                    onClick={() => setTab("schedule")}
                  >
                    Расписание
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={detailTab === "notify"}
                    className={
                      detailTab === "notify"
                        ? s.groupsHubDetailTabActive
                        : s.groupsHubDetailTab
                    }
                    onClick={() => setTab("notify")}
                  >
                    Явка
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={detailTab === "venue"}
                    className={
                      detailTab === "venue"
                        ? s.groupsHubDetailTabActive
                        : s.groupsHubDetailTab
                    }
                    onClick={() => setTab("venue")}
                  >
                    Зал
                  </button>
                </div>

                {detailTab === "venue" ? (
                  <div className={s.groupsHubVenueRow}>
                    <label className={s.groupsHubVenueLabel}>
                      Зал для группы
                      <Select
                        value={editBranchId}
                        onChange={(e) => setEditBranchId(e.target.value)}
                      >
                        {branches.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </Select>
                    </label>
                    <Button
                      type="button"
                      size="sm"
                      disabled={
                        savingBranch ||
                        editBranchId ===
                          (selected.branch_id || selected.branch?.id)
                      }
                      onClick={saveBranch}
                    >
                      {savingBranch ? "…" : "Сохранить зал"}
                    </Button>
                  </div>
                ) : detailTab === "schedule" ? (
                  <GroupSchedulePanel
                    groupId={selected.id}
                    groupName={selected.name}
                    directionName={dirName(selected)}
                  />
                ) : detailTab === "notify" ? (
                  <div className={s.groupsHubVenueRow}>
                    <p className={s.docSectionHint}>
                      За {pollHours} ч до тренировки — push и опрос в приложении
                      клиента.
                    </p>
                    <label className={s.branchCheckItem}>
                      <input
                        type="checkbox"
                        checked={pollAuto}
                        onChange={(e) => setPollAuto(e.target.checked)}
                      />
                      <span>Авто-опрос перед тренировкой</span>
                    </label>
                    <Field label="За сколько часов">
                      <Input
                        type="number"
                        min={1}
                        max={24}
                        value={pollHours}
                        disabled={!pollAuto}
                        onChange={(e) => setPollHours(e.target.value)}
                      />
                    </Field>
                    <Button
                      type="button"
                      size="sm"
                      disabled={savingPoll}
                      onClick={savePollSettings}
                    >
                      {savingPoll ? "…" : "Сохранить"}
                    </Button>
                  </div>
                ) : detailTab === "ranking" ? (
                  <GroupLeaderboardPanel groupId={selected.id} />
                ) : (
                  <>
                    <div className={s.groupsHubAddMember}>
                      <p className={s.groupsHubAddLabel}>
                        Добавить спортсмена в группу
                      </p>
                      <div className={s.groupsHubAddRow}>
                        <ClientSearchPicker
                          value={memberPatientId}
                          onChange={setMemberPatientId}
                        />
                        <Button
                          type="button"
                          disabled={!memberPatientId}
                          onClick={addMember}
                        >
                          Добавить
                        </Button>
                      </div>
                    </div>

                    <div className={s.groupsHubMembersBlock}>
                      <h3 className={s.groupsHubMembersTitle}>
                        Состав · {members.length}
                      </h3>
                      {members.length === 0 ? (
                        <p className={s.groupsHubMembersEmpty}>
                          Пока никого — найдите клиента выше или добавьте в
                          разделе «Клиенты»
                        </p>
                      ) : (
                        <ul className={s.groupsHubMembers}>
                          {members.map((m) => (
                            <li key={m.id} className={s.groupsHubMemberRow}>
                              <Link
                                to={`/manage/app/clients/${m.patient_id}`}
                                className={s.groupsHubMemberName}
                              >
                                {m.patient?.full_name || "Клиент"}
                              </Link>
                              {m.patient?.phone ? (
                                <a
                                  href={`tel:${m.patient.phone}`}
                                  className={s.groupsHubMemberPhone}
                                >
                                  {m.patient.phone}
                                </a>
                              ) : null}
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                onClick={async () => {
                                  await clinicAPI.removeGroupMember(
                                    selected.id,
                                    m.patient_id,
                                  );
                                  loadMembers(selected.id);
                                  load();
                                  toast.success("Убран из группы");
                                }}
                              >
                                Убрать
                              </Button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </section>
        </div>
      )}
      {!staffMode ? renderArchiveSheet() : null}
    </div>
  );
}
